import { Box, Typography } from '@mui/material'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import StarHalfIcon from '@mui/icons-material/StarHalf'

interface StarRatingProps {
  rating: number
  size?: 'small' | 'medium' | 'large'
  maxRating?: number
  showRating?: boolean
}

const StarRating = (props: StarRatingProps): JSX.Element => {
  const { rating, size = 'medium', maxRating = 5, showRating = false } = props

  const getStarSize = () => {
    switch (size) {
      case 'small':
        return { fontSize: 16 }
      case 'large':
        return { fontSize: 28 }
      default:
        return { fontSize: 20 }
    }
  }

  const getTypographySize = () => {
    switch (size) {
      case 'small':
        return 'caption'
      case 'large':
        return 'body1'
      default:
        return 'body2'
    }
  }

  const stars = []
  const fullStars = Math.floor(rating)
  const hasHalfStar = rating % 1 >= 0.5
  const emptyStars = maxRating - fullStars - (hasHalfStar ? 1 : 0)

  // Add full stars
  for (let i = 0; i < fullStars; i++) {
    stars.push(
      <StarIcon
        key={`full-${i}`}
        sx={{
          color: 'warning.main',
          ...getStarSize(),
          filter: 'drop-shadow(0 1px 2px rgba(255, 193, 7, 0.3))',
          transition: 'all 0.2s ease-in-out',
          animation: i < 2 ? 'pulse 2s ease-in-out infinite' : 'none',
          '@keyframes pulse': {
            '0%, 100%': {
              transform: 'scale(1)',
              opacity: 1,
            },
            '50%': {
              transform: 'scale(1.05)',
              opacity: 0.9,
            },
          },
        }}
      />,
    )
  }

  // Add half star if needed
  if (hasHalfStar) {
    stars.push(
      <StarHalfIcon
        key="half"
        sx={{
          color: 'warning.main',
          ...getStarSize(),
          filter: 'drop-shadow(0 1px 2px rgba(255, 193, 7, 0.3))',
          transition: 'all 0.2s ease-in-out',
        }}
      />,
    )
  }

  // Add empty stars
  for (let i = 0; i < emptyStars; i++) {
    stars.push(
      <StarBorderIcon
        key={`empty-${i}`}
        sx={{
          color: 'action.disabled',
          ...getStarSize(),
          opacity: 0.4,
          transition: 'all 0.2s ease-in-out',
        }}
      />,
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.3,
        position: 'relative',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.2,
          background:
            size === 'large'
              ? 'linear-gradient(135deg, rgba(255, 193, 7, 0.05) 0%, rgba(255, 152, 0, 0.05) 100%)'
              : 'transparent',
          padding: size === 'large' ? '4px 8px' : '0',
          borderRadius: 1,
          border: size === 'large' ? '1px solid' : 'none',
          borderColor: size === 'large' ? 'warning.light' : 'transparent',
        }}
      >
        {stars}
      </Box>
      {showRating && (
        <Typography
          variant={getTypographySize()}
          sx={{
            ml: 0.75,
            fontWeight: 600,
            color: 'text.primary',
            background: 'linear-gradient(135deg, #ff9800 0%, #ff6f00 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {rating.toFixed(1)}
        </Typography>
      )}
    </Box>
  )
}

export default StarRating
